import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { bankCardMetadata } from "@features/Budget/budgetConstants";
import { useBudgetFunctionalityDisabledError } from "@features/Budget/hooks/useBudgetFunctionalityDisabledError";
import { Button } from "@components/Button";
import { PlusIcon } from "@components/icons";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCreateSavingsPlanMutation } from "@services/budget/mutations";
import {
  SavingsPlanDataProviderType,
  type ISavingsPlanBankAccountsResponse,
  type TCreateSavingsPlanRequest,
} from "@services/budget/types";
import { formatDate, parseDecimalNumberInputValue } from "@utils/units";
import {
  useAddSavingsPlanSchema,
  type TAddSavingsPlanSchema,
} from "./useAddSavingsPlanSchema";

interface IAddSavingsPlanModalProps extends IUseModalProps {
  savingsPlanBankAccounts: ISavingsPlanBankAccountsResponse;
}

export const AddSavingsPlanModal: React.FC<IAddSavingsPlanModalProps> = (
  props,
) => {
  const { savingsPlanBankAccounts, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const { mutateAsync, isPending } = useCreateSavingsPlanMutation();

  const bankSelectOptions = [
    ...savingsPlanBankAccounts.bankAccounts.map((bank) => ({
      label: bank.iban,
      value: bank.bankAccountId,
      icon: bankCardMetadata[bank.bankName].iconSmall,
    })),
  ];

  const schema = useAddSavingsPlanSchema();

  const { control, setValue, getValues, handleSubmit, setError, watch } =
    useForm<TAddSavingsPlanSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        planName: "",
        planAmount: 0,
        achievedAmount: 0,
        savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.Manual,
        bankAccountId: savingsPlanBankAccounts.bankAccounts[0]?.bankAccountId,
      },
    });

  const savingsPlanDataProviderTypeId = watch("savingsPlanDataProviderTypeId");
  const bankAccountId = watch("bankAccountId");

  const currentBankIcon =
    bankCardMetadata[
      savingsPlanBankAccounts.bankAccounts.find(
        (bank) => bank.bankAccountId === bankAccountId,
      )?.bankName ?? "Default"
    ]?.iconSmall;

  const providerTypeOptions = [
    {
      label: t("budget.savings.savingsPlanDataProviderType.manual"),
      value: SavingsPlanDataProviderType.Manual,
    },
    {
      label: t("budget.savings.savingsPlanDataProviderType.bankAccountBalance"),
      value: SavingsPlanDataProviderType.BankAccountBalance,
    },
  ];

  const { setApiErrors } = useApiErrorUtils(setError);
  const { handleBudgetError } = useBudgetFunctionalityDisabledError();

  const onSubmit: SubmitHandler<TAddSavingsPlanSchema> = async (data) => {
    const createSavingsPlanRequest = (
      data: TAddSavingsPlanSchema,
    ): TCreateSavingsPlanRequest => {
      if (
        data.savingsPlanDataProviderTypeId ===
        SavingsPlanDataProviderType.BankAccountBalance
      ) {
        return {
          planName: data.planName,
          planAmount: parseDecimalNumberInputValue(data.planAmount.toString()),
          endDate: data.endDate
            ? formatDate(data.endDate, "yyyy-MM-dd")
            : undefined,
          savingsPlanDataProviderTypeId:
            SavingsPlanDataProviderType.BankAccountBalance,
          bankAccountId: data.bankAccountId!,
        };
      } else {
        return {
          planName: data.planName,
          planAmount: parseDecimalNumberInputValue(data.planAmount.toString()),
          endDate: data.endDate
            ? formatDate(data.endDate, "yyyy-MM-dd")
            : undefined,
          savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.Manual,
          achievedAmount: parseDecimalNumberInputValue(
            data.achievedAmount!.toString(),
          ),
        };
      }
    };

    const response = await mutateAsync(createSavingsPlanRequest(data)).catch(
      handleBudgetError(setApiErrors),
    );

    if (response) {
      toast.success(t("budget.savings.addSavingsPlanModal.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("budget.savings.addSavingsPlanModal.title")}
      subtitle={t("budget.savings.addSavingsPlanModal.subtitle")}
      badgeProps={{ icon: PlusIcon, color: "green" }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <FormInput
          control={control}
          name="planName"
          label={t("budget.savings.addSavingsPlanModal.planName")}
        />
        <FormInput
          control={control}
          name="planAmount"
          label={t("budget.savings.addSavingsPlanModal.planAmmount")}
          onBlur={() =>
            setValue(
              "planAmount",
              parseDecimalNumberInputValue(getValues("planAmount").toString()),
            )
          }
        />
        <FormDatepicker
          control={control}
          name="endDate"
          label={t("budget.savings.addSavingsPlanModal.endDate")}
        />
        <FormSelect
          name="savingsPlanDataProviderTypeId"
          control={control}
          label={t(
            "budget.savings.addSavingsPlanModal.savingsPlanDataProviderType",
          )}
          options={providerTypeOptions}
        />
        {savingsPlanDataProviderTypeId ===
          SavingsPlanDataProviderType.Manual && (
          <FormInput
            control={control}
            name="achievedAmount"
            label={t("budget.savings.addSavingsPlanModal.achievedAmount")}
            onBlur={() =>
              setValue(
                "achievedAmount",
                parseDecimalNumberInputValue(
                  getValues("achievedAmount")?.toString() ?? "0",
                ),
              )
            }
          />
        )}
        {savingsPlanDataProviderTypeId ===
          SavingsPlanDataProviderType.BankAccountBalance && (
          <FormSelect
            label={t("budget.savings.addSavingsPlanModal.bankAccount")}
            name="bankAccountId"
            control={control}
            options={bankSelectOptions}
            icon={currentBankIcon}
          />
        )}
      </div>
    </Modal>
  );
};
